import { logD } from "@blacknut/logging/dist";
import { NativeModule } from "@blacknut/playerwebrtc";
import { NotificationContextProvider } from "@blacknut/react-sdk/dist";
import { PureComponent, ReactNode } from "react";
import Player from "./components/Player/Player";
import RootView from "./components/RootView/RootView";
import { stop } from "./events";
import { WebRTCError } from "./model";
import { setDatadogStreamId } from "./utils";
import configHolder from "./utils/ConfigHolder";
import { ContextProvider } from "./utils/ContextProvider";
import { EricssonContextProvider } from "./utils/EricssonContext";
import { PlayerContextProvider } from "./utils/PlayerContext";
import { VGamepadContextProvider } from "./utils/VGamepadContext";
import { LOGGING_TAG } from "./utils/logging";
import { I18nextProvider } from "react-i18next";
import { apiService, FullscreenHelper } from "@blacknut/javascript-sdk/dist";
import React from "react";
interface StreamLauncherProps {
  gameId: string;
  gameParameters?: string[];
  onError: (e: WebRTCError) => void;
  onCancelLaunch: () => void;
  onStop: () => void;
  codecFilter?: string[];
  iceTransportPolicy?: RTCIceTransportPolicy;
  iceServers?: RTCIceServer[];
  acquireMicro?: boolean;
  onPlay?: () => void;
  onGameLoaded?: () => void;
  requestKeyboardLock?: () => Promise<void>;
  getKeyboardLayoutMap?: () => Promise<"azerty" | "qwerty" | "qwertz" | undefined>;

  preferedWindowMode?: "windowed" | "fullscreen";
  stats?: boolean;
  confirmExit?: boolean;
  embedded?: boolean;
  autoplay?: boolean;
  // pace for metrics to be retrieved
  statsInterval?: number;
  fullscreenHelper: FullscreenHelper;
  mute?: boolean;
  warmupvideo?: boolean;
}
interface GameLauncherState {
  mounted: boolean;
}
class GameLauncher extends PureComponent<StreamLauncherProps, GameLauncherState> {
  constructor(props) {
    super(props);
    this.state = { mounted: false };
  }
  private teardown = () => {
    //datadog configuration, let's ignore stream from now on
    setDatadogStreamId();
    setTimeout(() => {
      NativeModule?.module?.teardown();
    }, 200);
  };
  private _onError = (e: WebRTCError) => {
    const { onError } = this.props;
    NativeModule.module?.onError(e);
    onError(e);
    this.teardown();
  };

  private _onCanceled = () => {
    const { onCancelLaunch } = this.props;

    NativeModule.module?.onCancelLaunch?.();
    onCancelLaunch();
    this.teardown();
  };

  private _onStop = () => {
    const { onStop } = this.props;

    NativeModule.module?.onStop();
    onStop();
    this.teardown();
  };

  componentDidMount(): void {
    document.querySelector("html")?.classList.add("overflow-hidden");
    document.querySelector("html")?.classList.add("disable-select");
    const { embedded } = this.props;
    setDatadogStreamId();

    NativeModule.init(!!embedded);

    NativeModule.module?.onExit?.(stop);

    window.addEventListener("unload", this._unload);
    if (NativeModule.module?.getDeviceInfo) {
      try {
        NativeModule.module?.getDeviceInfo().then((res) => {
          if (res.userAgent) {
            apiService.userAgent = {
              ...apiService.userAgent,
              ...res.userAgent,
            };
          }
          this.setState({ mounted: true });
        });
      } catch (e) {
        this.setState({ mounted: true });
      }
    } else {
      this.setState({ mounted: true });
    }
  }

  componentWillUnmount(): void {
    document.querySelector("html")?.classList.remove("overflow-hidden");
    document.querySelector("html")?.classList.remove("disable-select");
    window.removeEventListener("unload", this._unload);
  }

  stop = () => {
    stop();
  };

  private _unload = () => {
    const { onStop } = this.props;

    logD(LOGGING_TAG, "Unload");
    this.teardown();
    onStop();
    NativeModule.module?.onStop();
    window.removeEventListener("unload", this._unload);
  };

  render(): ReactNode {
    const {
      gameId,
      gameParameters,
      codecFilter,
      iceTransportPolicy,
      iceServers,
      acquireMicro,
      onPlay,
      onGameLoaded,
      requestKeyboardLock,
      preferedWindowMode,
      getKeyboardLayoutMap,
      stats,
      confirmExit,
      embedded,
      statsInterval,
      fullscreenHelper,
      autoplay,
      mute,
      warmupvideo,
    } = this.props;
    if (!this.state.mounted) {
      return null;
    }
    return (
      <ContextProvider sdkConfig={configHolder.config}>
        <I18nextProvider i18n={configHolder.i18n!} defaultNS={["player"]}>
          <NotificationContextProvider>
            <PlayerContextProvider
              getKeyboardLayoutMap={getKeyboardLayoutMap}
              fullscreenHelper={
                NativeModule.module?.createFullScreenHelper?.() || fullscreenHelper
              }
              requestKeyboardLock={requestKeyboardLock}
              preferedWindowMode={preferedWindowMode}
            >
              <EricssonContextProvider>
                <RootView embedded={!!embedded}>
                  <VGamepadContextProvider>
                    <Player
                      onError={this._onError}
                      onStopStream={this._onStop}
                      codecFilter={codecFilter}
                      iceTransportPolicy={iceTransportPolicy}
                      iceServers={iceServers}
                      acquireMicro={acquireMicro}
                      onPlay={onPlay}
                      stats={stats}
                      confirmExit={confirmExit}
                      embedded={embedded}
                      statsInterval={statsInterval}
                      autoplay={autoplay}
                      mute={mute}
                      warmupvideo={warmupvideo}
                      gameId={gameId}
                      onGameLoaded={onGameLoaded}
                      onCancelLaunch={this._onCanceled}
                      gameParameters={gameParameters}
                    />
                  </VGamepadContextProvider>
                </RootView>
              </EricssonContextProvider>
            </PlayerContextProvider>
          </NotificationContextProvider>
        </I18nextProvider>
      </ContextProvider>
    );
  }
}

export { GameLauncher };
